import type { ReactNode } from 'react';

import { Button } from './Button';
import { cx } from './cx';
import { Icon } from './Icon';

/**
 * Pinned to the bottom of a settings page while a draft differs from what was loaded: how
 * many things changed on the left, Discard and Save on the right.
 *
 * It appears rather than sitting there greyed out, because a bar that is always present is
 * a bar nobody reads — and the count is what makes "Save" mean something specific rather
 * than "whatever I have been clicking for the last two minutes".
 */
export function SaveBar({
  changes,
  busy,
  onDiscard,
  onSave,
  discardLabel,
  saveLabel,
  className,
  problem,
  children,
}: {
  changes: number;
  /**
   * Why the last save was refused, said here as well as in the banner at the top: on a long
   * page the banner has scrolled away, and this bar is where the eye is after pressing Save.
   */
  problem?: ReactNode;
  busy?: boolean;
  onDiscard: () => void;
  onSave: () => void;
  discardLabel: ReactNode;
  saveLabel: ReactNode;
  /** The sentence saying how many changes are unsaved; the caller owns the words. */
  children?: ReactNode;
  className?: string;
}) {
  if (changes < 1) return null;

  return (
    <div
      className={cx(
        'sticky bottom-0 z-10 flex items-center justify-between gap-4 px-5 py-2.5 bg-surface border border-rule rounded-card shadow-float',
        className,
      )}
    >
      {problem ? (
        <span role="alert" className="flex items-center gap-1.5 min-w-0 text-secondary font-medium text-danger">
          <Icon name="alert" small />
          <span className="min-w-0">{problem}</span>
        </span>
      ) : (
        <span className="text-secondary text-ink-2">{children}</span>
      )}
      <div className="flex items-center gap-2">
        <Button variant="quiet" onClick={onDiscard} disabled={busy}>
          {discardLabel}
        </Button>
        <Button variant="primary" onClick={onSave} disabled={busy}>
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
